import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { ScrollAnimation } from "../ScrollAnimation";

export default function KAP() {
  const processSteps = [
    "Avaliação clínica criteriosa",
    "Uso da cetamina em doses controladas com acompanhamento terapêutico",
    "Integração de diferentes abordagens psicoterápicas, conforme a necessidade do paciente",
    "Três momentos terapêuticos: preparação, experiência assistida e integração",
    "Possibilidade de participação do terapeuta de referência",
    "Ambiente individual com suporte contínuo",
  ];

  return (
    <section id="cetamina" className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#faf9f7] overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start">
          <ScrollAnimation animation="fade-right" delay={200}>
            <div className="relative h-72 sm:h-96 lg:h-full min-h-72 lg:min-h-[500px] rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/WhatsApp%20Image%202026-03-17%20at%2018.19.09-Lbhmhu4VQgN9z31oeJ0C4lEOTCEhb3.jpeg"
                alt="Sala de psicoterapia assistida por cetamina"
                className="w-full h-full object-cover rounded-3xl"
              />
            </div>
          </ScrollAnimation>

          <div className="overflow-hidden">
            <ScrollAnimation animation="fade-up">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#C2A44A]" />
                <span className="font-sans text-sm font-semibold text-[#C2A44A] tracking-wider uppercase">Inovação</span>
              </div>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={100}>
              <h2 className="font-sans text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#204613] mb-4 text-balance leading-tight">
                Psicoterapia Assistida por Cetamina (PAC)
              </h2>
              <p className="font-serif text-lg sm:text-xl text-[#698658] italic mb-6">
                Integração entre ciência, experiência e cuidado individualizado
              </p>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={200}>
              <p className="font-serif text-base sm:text-lg text-[#4a4a4a] mb-5 leading-relaxed">
                A Psicoterapia Assistida por Cetamina é um modelo terapêutico que integra o uso da cetamina, em doses controladas, a um acompanhamento psicoterapêutico estruturado.
              </p>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={250}>
              <p className="font-serif text-base sm:text-lg text-[#4a4a4a] mb-5 leading-relaxed">
                A cetamina é utilizada há décadas na medicina como anestésico e, em doses subanestésicas, vem sendo empregada na psiquiatria, especialmente em casos de depressão e outros quadros complexos com resposta insuficiente aos tratamentos convencionais.
              </p>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={300}>
              <p className="font-serif text-base sm:text-lg text-[#4a4a4a] mb-5 leading-relaxed">
                Seu efeito está associado a mudanças na atividade cerebral e a mecanismos de neuroplasticidade, podendo favorecer maior flexibilidade de padrões emocionais e cognitivos.
              </p>
            </ScrollAnimation>
            <ScrollAnimation animation="fade-up" delay={350}>
              <p className="font-serif text-base sm:text-lg text-[#4a4a4a] mb-6 leading-relaxed">
                Durante a sessão, podem ocorrer alterações na percepção e no estado de consciência, com diferentes intensidades, que são acompanhadas pela equipe ao longo de toda a experiência. Esse estado pode facilitar o acesso e a elaboração de conteúdos emocionais relevantes.
              </p>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={400}>
              <div className="bg-[#ACC996]/10 border-l-4 border-[#C2A44A] rounded-r-xl p-4 sm:p-5 mb-6">
                <p className="font-sans text-base sm:text-lg font-semibold text-[#204613] mb-2">Indicação</p>
                <p className="font-serif text-base sm:text-lg text-[#4a4a4a] leading-relaxed">
                  A Psicoterapia Assistida por Cetamina pode ser considerada em casos selecionados, após avaliação clínica criteriosa. É indicada principalmente em quadros de depressão — especialmente quando há dificuldade de resposta aos tratamentos convencionais —, podendo também ser utilizada em situações como trauma psicológico, ansiedade, transtorno obsessivo-compulsivo e outros quadros complexos, sempre dentro de um plano terapêutico individualizado.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={450}>
              <p className="font-sans text-base sm:text-lg font-semibold text-[#204613] mb-4">Como conduzimos o tratamento</p>
              <p className="font-serif text-base sm:text-lg text-[#4a4a4a] mb-5 leading-relaxed">
                A administração ocorre em um setting individual, com a presença do médico e do terapeuta, garantindo acompanhamento contínuo e integrado. A psicoterapia é parte ativa da experiência, favorecendo a integração dos conteúdos vivenciados e a construção de mudanças ao longo do tempo.
              </p>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={500}>
              <p className="font-serif text-base sm:text-lg text-[#698658] mb-4 leading-relaxed">O processo inclui:</p>
            </ScrollAnimation>

            <div className="flex flex-col gap-3 mb-6">
              {processSteps.map((item, idx) => (
                <ScrollAnimation key={idx} animation="fade-up" delay={550 + idx * 60}>
                  <div className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-xl border border-[#ACC996]/20 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-[#C2A44A] mt-2 flex-shrink-0" />
                    <span className="font-serif text-sm sm:text-base text-[#3d3d3d] leading-relaxed">{item}</span>
                  </div>
                </ScrollAnimation>
              ))}
            </div>

            <ScrollAnimation animation="fade-up" delay={850}>
              <p className="font-serif text-xs sm:text-sm text-[#6b6b6b] italic mb-5">
                O uso da cetamina neste contexto é considerado off label, sendo realizado com base em evidências científicas atuais, mediante avaliação médica e consentimento informado.
              </p>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={900}>
              <p className="font-serif text-base sm:text-lg text-[#698658] italic mb-8">
                A combinação entre cetamina e psicoterapia, aplicada dentro de um cuidado estruturado e individualizado, favorece o acesso a conteúdos internos e amplia o processo terapêutico.
              </p>
            </ScrollAnimation>

            <ScrollAnimation animation="scale" delay={950}>
              <a
                href="#contato"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-[#C2A44A] text-white rounded-xl font-sans font-semibold text-sm sm:text-base hover:bg-[#b3943f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Agendar Avaliação
                <ArrowRight className="w-4 h-4" />
              </a>
            </ScrollAnimation>
          </div>
        </div>
      </div>
    </section>
  );
}
